import { useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { WarningOctagon, Trash, ShieldWarning } from "@phosphor-icons/react";

const TARGETS = [
  { key: "cars", label: "Stok Mobil", desc: "Semua data mobil (stok & terjual)" },
  { key: "customers", label: "Pelanggan", desc: "Semua data pelanggan" },
  { key: "sales", label: "Penjualan", desc: "Semua transaksi + jadwal cicilan" },
  { key: "payments", label: "Pembayaran", desc: "Semua riwayat bayar cicilan & DP" },
  { key: "purchases", label: "Pembelian", desc: "Semua data pembelian mobil" },
  { key: "car_expenses", label: "Biaya Mobil", desc: "Semua catatan biaya per mobil" },
  { key: "audit_logs", label: "Log Aktivitas", desc: "Riwayat audit sistem" },
];

export default function ResetData() {
  const [selected, setSelected] = useState({});
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const anySelected = Object.values(selected).some(Boolean);
  const allSelected = TARGETS.every((t) => selected[t.key]);
  const chosen = TARGETS.filter((t) => selected[t.key]);

  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(TARGETS.reduce((a, t) => ({ ...a, [t.key]: true }), {}));
  };

  const doReset = async () => {
    if (!password) { toast.error("Masukkan password Anda"); return; }
    if (confirmText.trim().toUpperCase() !== "HAPUS") {
      toast.error("Ketik 'HAPUS' untuk konfirmasi");
      return;
    }
    if (!anySelected) { toast.error("Pilih minimal satu jenis data"); return; }
    if (!window.confirm(`Yakin hapus ${chosen.length} jenis data? Tindakan ini TIDAK BISA dibatalkan.`)) return;
    setBusy(true);
    try {
      const targets = chosen.map((t) => t.key);
      const r = await api.post("/admin/reset-data", { password, targets });
      setResult(r.data);
      toast.success("Data berhasil dihapus");
      setPassword("");
      setConfirmText("");
      setSelected({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus data");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Reset Data" subtitle="Hapus data operasional secara massal — hanya admin, butuh password ulang" />
      <div className="p-4 md:p-6 lg:p-10 max-w-3xl">
        <div className="border-l-4 border-[#E63946] bg-red-50 p-5 mb-6">
          <div className="flex items-start gap-3">
            <WarningOctagon size={22} weight="fill" className="text-[#E63946] mt-0.5 shrink-0" />
            <div>
              <div className="font-display font-bold uppercase tracking-wider text-sm text-[#E63946]">Peringatan Bahaya</div>
              <div className="text-sm text-gray-700 mt-1">
                Data yang dihapus <b>TIDAK BISA dikembalikan</b>. Gunakan menu ini hanya untuk reset sistem, migrasi, atau setup awal.
                <b> Users, Settings, dan master data (bunga, kategori telat, katalog mobil) tidak akan disentuh.</b>
              </div>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium">Pilih Data yang Ingin Dihapus</div>
            <button
              type="button"
              onClick={toggleAll}
              data-testid="reset-toggle-all"
              className="text-xs uppercase tracking-wider border border-gray-900 px-3 py-1 hover:bg-gray-900 hover:text-white transition-colors"
            >
              {allSelected ? "Batal Pilih Semua" : "Pilih Semua"}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {TARGETS.map((t) => (
              <label
                key={t.key}
                data-testid={`reset-opt-${t.key}`}
                className={`border p-4 cursor-pointer transition-colors ${selected[t.key] ? "border-[#E63946] bg-red-50" : "border-gray-200 hover:border-gray-400"}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={!!selected[t.key]}
                    onChange={(e) => setSelected({ ...selected, [t.key]: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-[#E63946]"
                  />
                  <div>
                    <div className="font-semibold">{t.label}</div>
                    <div className="text-xs text-gray-500 mt-1">{t.desc}</div>
                  </div>
                </div>
              </label>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Password Anda</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Masukkan ulang password Anda"
                data-testid="reset-password"
                className="mt-2 rounded-none"
                autoComplete="current-password"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Ketik <b className="text-[#E63946] font-mono-num">HAPUS</b> untuk konfirmasi</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="HAPUS"
                data-testid="reset-confirm"
                className="mt-2 rounded-none font-mono-num tracking-widest"
              />
            </div>
          </div>

          <Button
            onClick={doReset}
            disabled={busy || !anySelected || !password || confirmText.trim().toUpperCase() !== "HAPUS"}
            data-testid="reset-submit"
            className="mt-6 w-full h-12 rounded-none bg-[#E63946] hover:bg-[#c62b39] text-white uppercase tracking-widest text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Trash size={16} className="mr-2" weight="bold" />
            {busy ? "Menghapus..." : `Hapus ${chosen.length} Jenis Data Sekarang`}
          </Button>

          {result && (
            <div className="mt-6 border border-emerald-200 bg-emerald-50 p-5" data-testid="reset-result">
              <div className="flex items-center gap-2 text-emerald-800">
                <ShieldWarning size={18} weight="bold" />
                <div className="font-semibold">Reset Selesai</div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm font-mono-num">
                {Object.entries(result.deleted).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-emerald-100 pb-1">
                    <span>{TARGETS.find((t) => t.key === k)?.label || k}</span>
                    <span className="font-semibold">{v} dihapus</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
